/**
 * PSMChart — Van Westendorp Price Sensitivity Meter (Quick Study `psm` plot).
 *
 * Legacy reader: results stored before plots plan G carry a full PSMResult on the
 * insight's `psm` field (its producer, `_psm_aggregate`, is deleted; new runs stamp
 * the canonical `price-curves` plot instead): four cumulative curves sampled on a SHARED price grid plus
 * the interpolated intersection points. There is no deck-ui chart for this shape, so
 * (mirroring the card's NpsDivergingBar) we draw it from SVG primitives — no new
 * dependency — as a four-line overlay with the key markers a marketer reads:
 *   • OPP  — Optimal Price Point (too-cheap × too-expensive), the headline number.
 *   • acceptable range [PMC, PME] — the band where the product is neither "too cheap"
 *     (quality doubt) nor "too expensive" (won't consider), drawn as a shaded span.
 *
 * Colours are SEMANTIC + theme-safe via the app's --chart-* tokens, matching the
 * NPS bar's convention: "too cheap"/"too expensive" (the rejection curves) read as
 * danger/red and neutral, "not cheap"/"expensive" (the value curves) read positive.
 *
 * Degenerate input (no curves, single price point, no crossings) never crashes: the
 * band/markers are simply omitted and an empty-state note renders instead of a chart.
 */
import { useMemo } from 'react'

// Mirrors models.PSMResult (serialized snake_case straight off result.model_dump()).
export type PsmCurvePoint = { price: number; cum_pct: number }
export type PsmData = {
  curves?: Record<string, PsmCurvePoint[]>
  opp?: number | null
  ipp?: number | null
  pmc?: number | null
  pme?: number | null
  acceptable_range?: (number | null)[]
  currency?: string
  n_valid?: number
  n_dropped_nonmonotonic?: number
}

// The four backend curve keys (with spaces, as the legacy producer wrote them), each with a DISTINCT
// semantic colour + display label so the overlay is readable: "too cheap" reads as a
// quality-doubt warning (amber), "not cheap"/good-value as positive (green), "expensive"
// as a caution (accent/orange), "too expensive" as outright rejection (red). All four
// resolve to existing --chart-* tokens (globals.css), theme-safe in light + dark.
const CURVE_DEFS: { key: string; label: string; color: string }[] = [
  { key: 'too cheap', label: 'Too cheap', color: 'hsl(var(--chart-warning))' },
  { key: 'not cheap', label: 'Not cheap', color: 'hsl(var(--chart-positive))' },
  { key: 'expensive', label: 'Expensive', color: 'hsl(var(--chart-accent))' },
  { key: 'too expensive', label: 'Too expensive', color: 'hsl(var(--chart-negative))' },
]

function fmtMoney(cur: string, v: number): string {
  // Whole dollars when clean, one decimal otherwise — matches the headline's _money feel.
  // Thousands separators so "$24000" reads as "$24,000" (matches the narrative copy).
  const n = Math.abs(v) >= 100 || Number.isInteger(v) ? Math.round(v) : Math.round(v * 10) / 10
  return `${cur}${n.toLocaleString('en-US')}`
}

/**
 * Van Westendorp four-curve price-sensitivity chart drawn from SVG primitives.
 * `data` is the insight's PSMResult; `currency` falls back to the data's own prefix.
 */
export function PSMChart({ data, testIdPrefix = 'qs-psm', showBaseSize = true }: { data: PsmData; testIdPrefix?: string; showBaseSize?: boolean }) {
  const cur = data?.currency || '$'

  // Collect the curves that actually carry points, in canonical order.
  const series = useMemo(() => {
    const curves = data?.curves ?? {}
    return CURVE_DEFS
      .map((d) => ({ ...d, points: Array.isArray(curves[d.key]) ? curves[d.key] : [] }))
      .filter((s) => s.points.length > 0)
  }, [data])

  // Price (x) + percent (y) domains across every plotted curve. y is always 0..100.
  const domain = useMemo(() => {
    let minP = Infinity
    let maxP = -Infinity
    for (const s of series) {
      for (const pt of s.points) {
        if (typeof pt.price !== 'number' || !Number.isFinite(pt.price)) continue
        if (pt.price < minP) minP = pt.price
        if (pt.price > maxP) maxP = pt.price
      }
    }
    return { minP, maxP }
  }, [series])

  // Empty / degenerate: no curves, or no real price spread to plot a line on.
  const degenerate = series.length === 0 || !Number.isFinite(domain.minP) || domain.maxP <= domain.minP
  if (degenerate) {
    return (
      <div
        className="flex h-40 items-center justify-center rounded-md bg-twin-chat-fg/5 text-xs text-twin-chat-fg-muted"
        data-testid={`${testIdPrefix}-empty`}
      >
        Not enough price spread to plot a price-sensitivity curve.
      </div>
    )
  }

  // Plot geometry. A fixed viewBox keeps the SVG crisp at any container width.
  const W = 520
  const H = 280
  const M = { top: 16, right: 16, bottom: 40, left: 44 }
  const plotW = W - M.left - M.right
  const plotH = H - M.top - M.bottom
  const { minP, maxP } = domain

  const xOf = (price: number) => M.left + ((price - minP) / (maxP - minP)) * plotW
  const yOf = (pct: number) => M.top + (1 - Math.max(0, Math.min(100, pct)) / 100) * plotH

  const pathFor = (points: PsmCurvePoint[]) =>
    points
      .filter((pt) => typeof pt.price === 'number' && Number.isFinite(pt.price))
      .map((pt, i) => `${i === 0 ? 'M' : 'L'}${xOf(pt.price).toFixed(1)},${yOf(pt.cum_pct).toFixed(1)}`)
      .join(' ')

  // Markers — only when the backend found a real crossing AND it sits inside the axis.
  const inRange = (v?: number | null): v is number =>
    typeof v === 'number' && Number.isFinite(v) && v >= minP && v <= maxP
  const opp = inRange(data?.opp) ? data!.opp! : null
  const [accLoRaw, accHiRaw] = data?.acceptable_range ?? [data?.pmc ?? null, data?.pme ?? null]
  const accLo = inRange(accLoRaw) ? accLoRaw : null
  const accHi = inRange(accHiRaw) ? accHiRaw : null
  const hasBand = accLo != null && accHi != null && accHi > accLo

  // Y gridlines at 0/25/50/75/100%.
  const yTicks = [0, 25, 50, 75, 100]
  // X ticks: a handful evenly across the price domain.
  const xTickCount = 4
  const xTicks = Array.from({ length: xTickCount + 1 }, (_, i) => minP + ((maxP - minP) * i) / xTickCount)

  const axisColor = 'hsl(var(--chart-neutral))'
  // OPP/band use the brand primary so the optimal-price marker + acceptable band stand
  // out from all four curve colours (which are already spoken for above).
  const oppColor = 'hsl(var(--chart-primary))'

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="flex items-baseline justify-between">
        <span className="text-xs text-twin-chat-fg-muted">Van Westendorp price sensitivity</span>
        {opp != null && (
          <span className="text-lg font-semibold text-twin-chat-fg" data-testid={`${testIdPrefix}-opp`}>
            {fmtMoney(cur, opp)}
          </span>
        )}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={`Van Westendorp price-sensitivity curves${opp != null ? `, optimal price about ${fmtMoney(cur, opp)}` : ''}${
          hasBand ? `, acceptable range ${fmtMoney(cur, accLo!)} to ${fmtMoney(cur, accHi!)}` : ''
        }`}
      >
        {/* Acceptable-range band [PMC, PME] behind the curves. */}
        {hasBand && (
          <rect
            x={xOf(accLo!)}
            y={M.top}
            width={Math.max(0, xOf(accHi!) - xOf(accLo!))}
            height={plotH}
            fill={oppColor}
            opacity={0.08}
            data-testid={`${testIdPrefix}-band`}
          />
        )}

        {/* Y gridlines + % labels. */}
        {yTicks.map((t) => (
          <g key={`y${t}`}>
            <line x1={M.left} y1={yOf(t)} x2={W - M.right} y2={yOf(t)} stroke={axisColor} strokeWidth={0.5} opacity={0.25} />
            <text x={M.left - 6} y={yOf(t) + 3} textAnchor="end" fontSize={9} fill="currentColor" className="text-twin-chat-fg-muted">
              {t}%
            </text>
          </g>
        ))}

        {/* X axis price labels. */}
        {xTicks.map((t, i) => (
          <text
            key={`x${i}`}
            x={xOf(t)}
            y={H - M.bottom + 16}
            textAnchor="middle"
            fontSize={9}
            fill="currentColor"
            className="text-twin-chat-fg-muted"
          >
            {fmtMoney(cur, t)}
          </text>
        ))}

        {/* The four cumulative curves. */}
        {series.map((s) => (
          <path
            key={s.key}
            d={pathFor(s.points)}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            data-testid={`${testIdPrefix}-curve-${s.key.replace(/\s+/g, '-')}`}
          />
        ))}

        {/* OPP marker: a vertical line + dot at the optimal price point. */}
        {opp != null && (
          <g data-testid={`${testIdPrefix}-opp-marker`}>
            <line x1={xOf(opp)} y1={M.top} x2={xOf(opp)} y2={M.top + plotH} stroke={oppColor} strokeWidth={1.5} strokeDasharray="4 3" />
            <circle cx={xOf(opp)} cy={M.top + 6} r={3} fill={oppColor} />
          </g>
        )}
      </svg>

      {/* Legend + the read-out figures. */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-twin-chat-fg-muted">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-twin-chat-fg-muted">
        {opp != null && (
          <span>
            Optimal price <span className="font-medium text-twin-chat-fg">{fmtMoney(cur, opp)}</span>
          </span>
        )}
        {hasBand && (
          <span data-testid={`${testIdPrefix}-acceptable`}>
            Acceptable{' '}
            <span className="font-medium text-twin-chat-fg">
              {fmtMoney(cur, accLo!)}–{fmtMoney(cur, accHi!)}
            </span>
          </span>
        )}
      </div>
      {/* On the chat surface (`showBaseSize={false}`) the PlotCard footnote already carries
          "Base: N twins" — mirror NpsDivergingBar's showBaseSize gate to avoid a double
          base line. The out-of-order-drop caveat is unique info, so it stays regardless. */}
      {typeof data?.n_valid === 'number' && (showBaseSize || Boolean(data?.n_dropped_nonmonotonic)) && (
        <p className="text-[11px] text-twin-chat-fg-muted">
          {showBaseSize ? `n=${data.n_valid}` : ''}
          {data?.n_dropped_nonmonotonic
            ? `${showBaseSize ? ' ' : ''}(${data.n_dropped_nonmonotonic} out-of-order dropped)`
            : ''}
        </p>
      )}
    </div>
  )
}
